/**
 * Rudderstack JS SDK singleton for the LimePhone web app.
 *
 * Source: limephone-web (workspace purple-sg)
 * Destinations attached: ga4-limephone + posthog-limephone + hub webhook.
 * Rudderstack owns explicit business events (track/identify/page/group) and
 * fans them out to the destinations above.
 *
 * posthog-js also runs on the page in parallel (see ./posthog.ts) but ONLY
 * for session replay, autocapture heatmaps, and $pageview — it is not an
 * event client. rsAnalytics.identify() mirrors into posthog.identify() so
 * session recordings are attached to the real user.
 *
 * Env vars (Vite):
 *   VITE_RUDDERSTACK_WRITE_KEY       — limephone-web source write key
 *   VITE_RUDDERSTACK_DATA_PLANE_URL  — workspace data plane URL
 *
 * If either is missing, `rsAnalytics` is a no-op proxy so call sites never
 * throw during local dev.
 */
import { RudderAnalytics } from '@rudderstack/analytics-js';
import posthog from 'posthog-js';

const writeKey = import.meta.env.VITE_RUDDERSTACK_WRITE_KEY as string | undefined;
const dataPlaneUrl = import.meta.env.VITE_RUDDERSTACK_DATA_PLANE_URL as string | undefined;

let instance: RudderAnalytics | null = null;

if (writeKey && dataPlaneUrl) {
  instance = new RudderAnalytics();
  // Share anonymousId across limephone.io (Webflow marketing) and
  // *.limephone.io (app, dashboard, m.*) via a domain-wide cookie so a
  // visitor who signs up has one continuous session across the boundary.
  // The leading dot is ignored by modern browsers (same effect) but keeps
  // legacy cookie parsers happy.
  instance.load(writeKey, dataPlaneUrl, {
    integrations: { All: true },
    setCookieDomain: '.limephone.io',
  });
}

type TrackFn = RudderAnalytics['track'];
type IdentifyFn = RudderAnalytics['identify'];
type PageFn = RudderAnalytics['page'];
type GroupFn = RudderAnalytics['group'];
type ResetFn = RudderAnalytics['reset'];

const noop = () => undefined;

/**
 * Public handle. Mirrors the Rudderstack API surface (track/identify/page/group)
 * but falls back to no-op when env vars aren't set so dev builds don't need
 * Rudderstack to run.
 */
function posthogIsLoaded(): boolean {
  try {
    return typeof posthog !== 'undefined' && typeof posthog.identify === 'function';
  } catch {
    return false;
  }
}

export const rsAnalytics: {
  track: TrackFn;
  identify: IdentifyFn;
  page: PageFn;
  group: GroupFn;
  reset: ResetFn;
  ready: boolean;
} = {
  track: ((...args: Parameters<TrackFn>) => instance?.track(...args) ?? noop()) as TrackFn,
  // identify() mirrors into posthog-js so session recordings attach to the
  // authenticated user. Rudderstack still routes the identify event to its
  // destinations (PostHog cloud API, GA4 user-id binding).
  identify: ((...args: Parameters<IdentifyFn>) => {
    const r = instance?.identify(...args);
    if (posthogIsLoaded() && typeof args[0] === 'string') {
      try {
        posthog.identify(args[0], (args[1] as Record<string, unknown> | undefined) ?? undefined);
      } catch {
        /* posthog not ready — fine, session replay just won't be user-bound */
      }
    }
    return r ?? noop();
  }) as IdentifyFn,
  page: ((...args: Parameters<PageFn>) => instance?.page(...args) ?? noop()) as PageFn,
  group: ((...args: Parameters<GroupFn>) => instance?.group(...args) ?? noop()) as GroupFn,
  reset: ((...args: Parameters<ResetFn>) => {
    const r = instance?.reset(...args);
    if (posthogIsLoaded()) {
      try {
        posthog.reset();
      } catch {
        /* noop */
      }
    }
    return r ?? noop();
  }) as ResetFn,
  ready: instance !== null,
};
